import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { AlertCircle, Droplet, Gauge, Info, Loader2, RefreshCw, Wand2, X } from 'lucide-react';
import type { Core, EventObject, NodeSingular } from 'cytoscape';
import type { FcoseLayoutOptions } from 'cytoscape-fcose';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PageContainer } from '@/components/ui/page-container';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useTheme } from '@/hooks/useTheme';
import NetworkGraph, { NetworkGraphHandle } from '../../components/NetworkGraph';
import DeviceDetailPanel, { type InterfaceConnectionMap, type DeviceLayerContext } from '../../components/DeviceDetailPanel';
import LinkDetailPanel from '../../components/LinkDetailPanel';
import EndpointPanel from '../../components/EndpointPanel';
import EndpointLinkPanel from '../../components/EndpointLinkPanel';
import { TopologyResyncDialog } from '../../components/views/TopologyResyncDialog';
import InterfaceDetailPanel from '../../components/InterfaceDetailPanel';
import { useAuth } from '../../hooks/useAuth';
import { useRegions } from '../../hooks/useRegions';
import { useTopology, type ConnectionEndpoint } from '../../hooks/useTopology';
import { useTopologyCacheSync } from '../../hooks/useWebSocketCacheSync';
import { useThroughputMetrics } from '../../hooks/useThroughputMetrics';
import { useStpOverlay, stpInstanceLabel } from '../../hooks/useStpOverlay';
import {
  useTopologyFindings,
  summarizeFindingsByDevice,
  severityColor,
  severityNeedsDarkText,
} from '../../hooks/useTopologyFindings';
import { transformTopologyToElements, speedBpsToCategory } from '../../utils/topologyTransform';
import { LayerType, LAYER_CONFIGS, OverlayType, OVERLAY_CONFIGS } from '../../types/layers';
import { VIEWS_RANGES, graphQueryFor, panelRange, viewsRangeLabel, type ViewsRange } from '../../components/traffic/range';
import { TopTalkersRail } from '../../components/traffic/TopTalkersRail';
import { applyLayer, getVlanColor, getWirelessBandColor, renderFindingsLayer,
  maxUtilization, renderPathLayer } from '../../utils/layerRenderers';
import { useGraphPositions } from '../../hooks/useGraphPositions';
import { applyPositionsToElements } from '../../utils/graphPositions';
import { positionEndpointSatellites } from '../../utils/endpointSatellites';
import {
  getIconTheme,
  getAvailableThemes,
  getSavedThemeId,
  saveThemeId,
} from '../../utils/iconThemes';

// Type for link data extracted from edge
interface LinkData {
  id: string;
  connectionType: string;
  confidenceScore: number;
  sourceEndpoint: ConnectionEndpoint;
  targetEndpoint: ConnectionEndpoint;
  physical?: {
    speed: string;
    speedBps?: number;
  };
}

/** Overlay ids that are active when the `overlays` URL param is absent. */
const DEFAULT_OVERLAYS = new Set<OverlayType>(
  (Object.keys(OVERLAY_CONFIGS) as OverlayType[]).filter(
    (id) => OVERLAY_CONFIGS[id].defaultOn
  )
);

/**
 * Parse the `overlays` URL param into the active overlay set.
 *
 * `null` (param absent from the URL) resolves to the configured defaults.
 * Any other value — including `''` (param present but empty) — resolves to
 * exactly the comma-separated set named, with unknown names ignored. This
 * distinction (absent vs. present-but-empty) is why callers must pass
 * `searchParams.get('overlays')` directly rather than `?? ''`.
 */
function parseOverlaysParam(param: string | null): Set<OverlayType> {
  if (param === null) return new Set(DEFAULT_OVERLAYS);
  const ids = param
    .split(',')
    .filter((id): id is OverlayType => id in OVERLAY_CONFIGS);
  return new Set(ids);
}

export default function Views() {
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  // URL search params for deep linking and browser history
  const [searchParams, setSearchParams] = useSearchParams();

  // Active overlay set — derived directly from the `overlays` URL param,
  // independent of the active visualization layer (orthogonal, stackable
  // toggles rather than the layer tabs' mutually-exclusive lenses). Computed
  // early (ahead of the topology fetch below) because the endpoints overlay
  // flag needs to reach `useTopology`'s `includeEndpoints` param.
  const activeOverlays = useMemo<Set<OverlayType>>(
    () => parseOverlaysParam(searchParams.get('overlays')),
    [searchParams]
  );
  const findingsOverlayOn = activeOverlays.has('findings');
  // S0 path overlay: `?path=` carries an ordered, comma-separated device-id
  // list (deep-linked from Path Check's "Open in Views").
  const pathOverlayOn = activeOverlays.has('path');
  const pathDeviceIds = useMemo(() => {
    const raw = searchParams.get('path');
    if (!raw) return [] as string[];
    return raw.split(',').filter((id) => id.length > 0);
  }, [searchParams]);
  const endpointsOverlayOn = activeOverlays.has('endpoints');

  // Fetch regions for the selector
  const { data: regions, isLoading: regionsLoading } = useRegions(organizationId);

  // Track selected region (default to first region when available)
  const [selectedRegionId, setSelectedRegionId] = useState<string | undefined>();

  // Track selected device for detail panel - initialize from URL params
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(
    () => searchParams.get('device')
  );

  // Track highlighted interfaces (when clicking from link detail panel) - initialize from URL params
  // This is an array to support highlighting LAG members along with the LAG interface
  const [highlightedInterfaceIds, setHighlightedInterfaceIds] = useState<string[]>(() => {
    const interfaceId = searchParams.get('interface');
    return interfaceId ? [interfaceId] : [];
  });

  // Track selected link for detail panel
  const [selectedLinkData, setSelectedLinkData] = useState<LinkData | null>(null);

  // Track pending link ID from URL (will be resolved when graph is ready)
  const [pendingLinkId, setPendingLinkId] = useState<string | null>(
    () => searchParams.get('link')
  );

  // Track selected interface for unconnected ports (no link in topology)
  // Stores both deviceId and interfaceId to show InterfaceDetailPanel
  const [selectedUnconnectedInterface, setSelectedUnconnectedInterface] = useState<{
    deviceId: string;
    interfaceId: string;
  } | null>(null);

  // Sync device/interface/link state with URL params when the user navigates with
  // back/forward. During render, on each URL change; the state starts from the URL,
  // so there is nothing to do on mount. (The layer needs no syncing: it is read
  // straight from the URL.)
  const [syncedParams, setSyncedParams] = useState(searchParams);
  if (searchParams !== syncedParams) {
    setSyncedParams(searchParams);
    const deviceFromUrl = searchParams.get('device');
    const interfaceFromUrl = searchParams.get('interface');
    const linkFromUrl = searchParams.get('link');

    // Update pending link ID when URL changes
    if (linkFromUrl !== pendingLinkId) {
      setPendingLinkId(linkFromUrl);
    }

    // Handle device selection from URL (link resolution handled separately)
    if (!linkFromUrl && deviceFromUrl) {
      if (deviceFromUrl !== selectedDeviceId) {
        setSelectedDeviceId(deviceFromUrl);
        setSelectedLinkData(null);
      }
      // Update highlighted interfaces from URL (just the primary - LAG members derived on click)
      const newHighlighted = interfaceFromUrl ? [interfaceFromUrl] : [];
      if (JSON.stringify(newHighlighted) !== JSON.stringify(highlightedInterfaceIds)) {
        setHighlightedInterfaceIds(newHighlighted);
      }
    } else if (!linkFromUrl && !deviceFromUrl) {
      // No params - clear selections if they exist
      if (selectedDeviceId || selectedLinkData) {
        setSelectedDeviceId(null);
        setSelectedLinkData(null);
        setHighlightedInterfaceIds([]);
      }
    }
  }

  // Auto-select first region when regions load
  if (!selectedRegionId && regions && regions.length > 0) {
    setSelectedRegionId(regions[0].id);
  }

  // Fetch topology data for selected region. `includeEndpoints` is gated on
  // the endpoints overlay toggle so the (potentially large) endpoints array
  // isn't fetched/held in memory when the overlay is off.
  const { data: topologyData, isLoading: topologyLoading, error: topologyError } = useTopology({
    organizationId,
    regionId: selectedRegionId,
    includeEndpoints: endpointsOverlayOn,
  });

  // Real-time topology updates via WebSocket
  // This will invalidate the topology query when devices/connections change
  useTopologyCacheSync(selectedRegionId);

  // Position management hook
  const {
    getPlacement,
    savePositions,
    clearCachedPositions,
  } = useGraphPositions({
    regionId: selectedRegionId,
  });

  // Get node IDs from topology data
  const nodeIds = useMemo(() => {
    if (!topologyData) return [];
    return topologyData.devices.map((d) => d.id);
  }, [topologyData]);

  // Icon theme for node icons (persisted in localStorage)
  const [iconThemeId, setIconThemeId] = useState<string>(() => getSavedThemeId());
  const iconTheme = useMemo(() => getIconTheme(iconThemeId), [iconThemeId]);
  const availableThemes = useMemo(() => getAvailableThemes(), []);

  // Handle theme change
  const handleThemeChange = useCallback((themeId: string) => {
    setIconThemeId(themeId);
    saveThemeId(themeId);
  }, []);

  // Placement (cached positions + pinned/unplaced sets) for the current node set.
  // Split out from `elements` so the auto-layout effect below can inspect
  // `pinnedIds`/`unplacedIds` without recomputing the transform.
  const placement = useMemo(() => getPlacement(nodeIds), [getPlacement, nodeIds]);

  // Transform topology data to Cytoscape elements with positions
  const elements = useMemo(() => {
    if (!topologyData) return [];
    const baseElements = transformTopologyToElements(topologyData, {
      iconTheme,
      endpoints: endpointsOverlayOn ? (topologyData.endpoints ?? []) : [],
    });
    const withDevicePositions = applyPositionsToElements(baseElements, placement.positions);

    // Endpoint satellite nodes (and their overflow node) don't get a cached
    // position of their own — `positionEndpointSatellites` fans them out
    // procedurally around their parent device once the graph mounts (see
    // the effect below). But without SOME starting position they'd render
    // at (0,0) for one frame before that effect runs, which reads as a
    // flash/jump. Seed them at their parent device's position instead, so
    // they visually "fan out from" the parent rather than "fly in from
    // the corner." The transform itself stays pure/position-agnostic; this
    // post-processing step is the one place that knows about placement.
    return withDevicePositions.map((el) => {
      if ('source' in el.data) return el; // edge — nothing to position
      if (el.position) return el; // device node — already positioned above
      const parentDeviceId = el.data.parentDeviceId as string | undefined;
      if (!parentDeviceId || !(parentDeviceId in placement.positions)) return el;
      const parentPos = placement.positions[parentDeviceId];
      return { ...el, position: { x: parentPos.x, y: parentPos.y } };
    });
  }, [topologyData, placement, iconTheme, endpointsOverlayOn]);

  // Extract unique VLANs from topology data for the legend
  const uniqueVlans = useMemo(() => {
    if (!topologyData) return [];

    const vlanMap = new Map<number, { vlanId: number; names: Set<string> }>();

    // Collect VLANs from all devices, gathering all unique names per VLAN ID
    topologyData.devices.forEach((device) => {
      if (device.vlans) {
        device.vlans.forEach((vlan) => {
          const existing = vlanMap.get(vlan.vlanId);
          const name = vlan.vlanName || `VLAN ${String(vlan.vlanId)}`;

          if (existing) {
            existing.names.add(name);
          } else {
            vlanMap.set(vlan.vlanId, {
              vlanId: vlan.vlanId,
              names: new Set([name]),
            });
          }
        });
      }
    });

    // Convert to array with names joined, sorted by VLAN ID
    return Array.from(vlanMap.values())
      .map((v) => ({
        vlanId: v.vlanId,
        displayName: `${String(v.vlanId)}: ${Array.from(v.names).join(', ')}`,
      }))
      .sort((a, b) => a.vlanId - b.vlanId);
  }, [topologyData]);

  // Build a map of interface ID -> connected remote endpoint for tooltip display
  // Only builds the map for the currently selected device
  const interfaceConnectionMap = useMemo((): InterfaceConnectionMap => {
    const map: InterfaceConnectionMap = new Map();
    if (!topologyData || !selectedDeviceId) return map;

    topologyData.connections.forEach((conn) => {
      // Check if source matches selected device
      if (conn.source.deviceId === selectedDeviceId && conn.source.interfaceId) {
        map.set(conn.source.interfaceId, {
          deviceId: conn.target.deviceId,
          deviceName: conn.target.deviceName,
          deviceIp: conn.target.deviceIp,
          interfaceName: conn.target.interfaceName,
        });
      }
      // Check if target matches selected device
      if (conn.target.deviceId === selectedDeviceId && conn.target.interfaceId) {
        map.set(conn.target.interfaceId, {
          deviceId: conn.source.deviceId,
          deviceName: conn.source.deviceName,
          deviceIp: conn.source.deviceIp,
          interfaceName: conn.source.interfaceName,
        });
      }
    });
    return map;
  }, [topologyData, selectedDeviceId]);

  // Selecting from the rail is the same act as clicking the edge, so it goes
  // through the same pending-link path the URL uses rather than a second one
  // that could drift from it.
  const handleRailSelect = useCallback((connectionId: string) => {
    setPendingLinkId(connectionId);
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      params.set('link', connectionId);
      params.delete('device');
      params.delete('interface');
      params.delete('unconnected');
      params.delete('endpoint');
      params.delete('endpointLink');
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // The panels mount below a fixed-height canvas, so on a 1080p screen their top
  // edge is already off-screen when the reader clicks. Without this, selecting a
  // node or an edge changes nothing visible and every ordering decision inside
  // those panels is moot.
  const panelRef = useRef<HTMLDivElement>(null);
  const selectionKey =
    selectedDeviceId ??
    selectedLinkData?.id ??
    selectedUnconnectedInterface?.interfaceId ??
    searchParams.get('endpointLink') ??
    searchParams.get('endpoint') ??
    null;
  useEffect(() => {
    if (selectionKey === null) return;
    // Honour a reader who asked the platform for less motion; scrolling a page
    // under someone is exactly what that setting is about.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    panelRef.current?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }, [selectionKey]);

  // Every interface topology found a link on, across the whole graph rather than
  // just the selected device: the interface panel is reachable for a connected
  // port as well as an unconnected one, and it states "Not connected" only when
  // this says so. Undefined membership would be a guess, so the map is built
  // from the same connections the canvas draws.
  const connectedInterfaceIds = useMemo((): Set<string> => {
    const ids = new Set<string>();
    if (!topologyData) return ids;
    topologyData.connections.forEach((conn) => {
      if (conn.source.interfaceId) ids.add(conn.source.interfaceId);
      if (conn.target.interfaceId) ids.add(conn.target.interfaceId);
    });
    return ids;
  }, [topologyData]);

  // Memoize layout configuration - use preset since we provide positions
  const graphLayout = useMemo(() => ({
    name: 'preset' as const,
    fit: true,
    padding: 30,
  }), []);

  // Resolved UI theme — drives the graph's background + label/edge colors.
  const { resolved: resolvedTheme } = useTheme();

  // Memoize style configuration to prevent graph reloads. In dark mode the
  // background is transparent so the themed `bg-card` container shows through.
  const graphStyle = useMemo(() => ({
    width: '100%',
    height: '700px',
    background: resolvedTheme === 'dark' ? 'transparent' : '#fafafa',
  }), [resolvedTheme]);

  // Ref to access NetworkGraph's imperative handle for layer management
  const graphRef = useRef<NetworkGraphHandle>(null);

  // The live Cytoscape instance as REACTIVE STATE. Effects that register cy
  // event handlers (or run one-shot work against the instance) must use
  // this, not `graphRef.current?.getCy()`: StrictMode's dev unmount/remount
  // of a newly mounted NetworkGraph replaces the instance without re-running
  // this component's effects, so imperative grabs can bind to a destroyed
  // graph. `onCyChanged` keeps this in sync with the real instance.
  const [liveCy, setLiveCy] = useState<Core | null>(null);

  // Runs a one-shot force-directed (fcose) layout to arrange nodes that
  // don't yet have a cached position, while holding pinned (user-dragged)
  // nodes fixed via fcose's fixedNodeConstraint. `fresh` distinguishes a
  // brand-new/just-reset graph (nothing placed at all — full quality
  // layout that frames the result) from an incremental one (some nodes
  // already placed, a few new ones appeared — existing nodes should only
  // adjust from their current positions without yanking the viewport).
  const runAutoLayout = useCallback((cy: Core, pinnedIds: string[], fresh: boolean) => {
    const fixedNodeConstraint = pinnedIds.length > 0
      ? pinnedIds.map((id) => {
          // position() returns a live object, so copy the coordinates.
          const pos = cy.getElementById(id).position();
          return { nodeId: id, position: { x: pos.x, y: pos.y } };
        })
      : undefined;

    const options: FcoseLayoutOptions = {
      name: 'fcose',
      quality: 'default', // fixedNodeConstraint requires default/proof quality
      randomize: fresh,
      fit: fresh,
      animate: true,
      animationDuration: 500,
      padding: 30,
      // Account for the label box under each node so tiling/packing/overlap
      // removal don't overlap labels (long device names widen the node box).
      nodeDimensionsIncludeLabels: true,
      // Zero-degree nodes (no discovered connections — the common brand-new
      // network case) get TILED into a grid; the default 10px padding packs
      // 50px icons nearly edge-to-edge, which reads as a cramped clump.
      tilingPaddingVertical: 60,
      tilingPaddingHorizontal: 60,
      fixedNodeConstraint,
    };

    // Run the layout on the infrastructure subgraph only — endpoint
    // satellite nodes/edges (and the overflow node/edge) are positioned
    // procedurally around their parent device (see `positionEndpointSatellites`
    // and its glue effect below), not by fcose. fcose doesn't know their
    // "orbit" semantics and would otherwise treat them as regular graph
    // nodes to pack/space, fighting the satellite placement on every layout.
    //
    // cy.layout()'s parameter type is cytoscape core's LayoutOptions union,
    // which doesn't include the fcose extension's option shape; fcose is
    // registered via cytoscape.use(fcose) in NetworkGraph.tsx. Collections
    // support `.layout()` with the same signature as `cy.layout()`.
    const infraElements = cy
      .elements()
      .filter((el) => !el.data('isEndpoint') && !el.data('isEndpointOverflow') && !el.data('endpointLink'));
    const layout = infraElements.layout(options);
    // Persist the result as soft (non-pinned) positions once the layout
    // settles, so subsequent placements see these nodes as already cached.
    layout.one('layoutstop', () => {
      savePositions(cy, nodeIds);
    });
    layout.run();
  }, [nodeIds, savePositions]);

  // Trigger auto-layout whenever there are unplaced nodes. Dedupe via a ref
  // keyed on the region + sorted unplaced-id set: without it, this effect
  // would re-run (and re-layout) on every unrelated re-render that shares
  // the same still-unplaced nodes, including if a failed localStorage save
  // caused the "unplaced" set to look unchanged after layoutstop — that
  // would otherwise loop. Effect ordering note: React runs child effects
  // before parent effects, so by the time this runs, NetworkGraph's own
  // elements-update effect has already applied the current `elements` to
  // the cy instance.
  const autoLayoutKeyRef = useRef<string>('');
  const autoLayoutCyRef = useRef<Core | null>(null);
  useEffect(() => {
    if (!selectedRegionId) return;
    if (placement.unplacedIds.length === 0) return;
    if (!liveCy) return; // graph only renders once elements exist

    // Dedupe on (key, instance) — a StrictMode remount swaps the instance
    // under an unchanged key, and the layout must re-run on the survivor or
    // the fresh graph never gets laid out.
    const key = `${selectedRegionId}:${[...placement.unplacedIds].sort().join(',')}`;
    if (autoLayoutKeyRef.current === key && autoLayoutCyRef.current === liveCy) return;
    autoLayoutKeyRef.current = key;
    autoLayoutCyRef.current = liveCy;

    const fresh =
      placement.pinnedIds.length === 0 && placement.unplacedIds.length === nodeIds.length;
    runAutoLayout(liveCy, placement.pinnedIds, fresh);
  }, [selectedRegionId, placement, nodeIds, runAutoLayout, liveCy]);

  // Endpoint satellite positioning (endpoints overlay). Satellite nodes are
  // excluded from both fcose (see `runAutoLayout` above) and the cached
  // position system (see `useGraphPositions.savePositions`), so something
  // has to keep them fanned out around their parent device as it moves.
  // That's this effect: place everything once on mount/overlay-toggle, then
  // keep re-placing as the graph changes shape underneath them.
  useEffect(() => {
    if (!endpointsOverlayOn) return;
    const cy = liveCy;
    if (!cy) return;

    positionEndpointSatellites(cy);

    // A device moving (drag, or fcose settling one node at a time) should
    // re-fan just that device's satellites. Without the isEndpoint/
    // isEndpointOverflow guard, `node.position(...)` inside
    // positionEndpointSatellites would itself fire this same handler for
    // the satellite it just moved — an infinite loop. The guard also
    // matters now that satellites are grabbable: while the user drags a
    // satellite, its own move events must not trigger a re-fan that would
    // fight the drag (their custom spot then survives until the parent
    // next moves or a layout runs, which re-rings them — deliberate).
    //
    // Listen on 'position' AND 'drag': interactive drags emit both
    // (renderer does silentShift + emit('position') + emit('drag')), while
    // programmatic/animated moves emit only 'position' — covering both
    // makes satellites track the parent however it moved. 'dragfree' adds
    // a final settle at the drop point.
    const handlePositionChange = (event: EventObject) => {
      const node = event.target as NodeSingular;
      if (node.data('isEndpoint') || node.data('isEndpointOverflow')) return;
      positionEndpointSatellites(cy, node.id());
    };
    // fcose animates nodes to their final spot over `animationDuration` —
    // reacting to every intermediate 'position' tick would work but is
    // wasteful and can visually lag; a full re-place on 'layoutstop' once
    // the animation settles covers that case cleanly.
    const handleLayoutStop = () => {
      positionEndpointSatellites(cy);
    };

    cy.on('position drag dragfree', 'node', handlePositionChange);
    cy.on('layoutstop', handleLayoutStop);

    return () => {
      cy.off('position drag dragfree', 'node', handlePositionChange);
      cy.off('layoutstop', handleLayoutStop);
    };
  }, [elements, endpointsOverlayOn, liveCy]);

  // Resolve pending link ID from URL once the graph is ready. Read from `elements` —
  // the definitions the graph is built from — rather than the live instance, so this
  // can run during render. An edge is the element with a source.
  const pendingEdge =
    pendingLinkId && liveCy
      ? elements.find((el) => el.data.id === pendingLinkId && el.data.source !== undefined)
      : undefined;
  if (pendingLinkId && pendingEdge) {
    const data = pendingEdge.data as {
      connectionType: string;
      confidenceScore: number;
      sourceEndpoint: ConnectionEndpoint;
      targetEndpoint: ConnectionEndpoint;
      physical?: { speed: string; speedBps?: number };
    };

    // Only update if we don't already have this link selected
    if (selectedLinkData?.id !== pendingLinkId) {
      setSelectedLinkData({
        id: pendingLinkId,
        connectionType: data.connectionType,
        confidenceScore: data.confidenceScore,
        sourceEndpoint: data.sourceEndpoint,
        targetEndpoint: data.targetEndpoint,
        physical: data.physical,
      });
      setSelectedDeviceId(null);
      setHighlightedInterfaceIds([]);
    }
    setPendingLinkId(null); // Clear pending once resolved
  }

  // Active visualization layer — the `layer` URL param is its only source, as with
  // the overlays: `handleLayerChange` writes the URL and back/forward rewrites it.
  const layerParam = searchParams.get('layer');
  const activeLayer: LayerType =
    layerParam && layerParam in LAYER_CONFIGS ? (layerParam as LayerType) : 'topology';

  // Selected VLAN for highlighting (convex hull)
  const [selectedVlanId, setSelectedVlanId] = useState<number | null>(null);

  // Selected STP instance for the STP layer. Stored as a tuple
  // because the same numeric instance_id is reused across protocol
  // variants (RSTP-0 and MSTP-CIST both report 0) — selection has
  // to key on (id, type) or the legend can't distinguish them.
  // Defaults to the first instance the backend returns, which is
  // sorted most-reported-first then by id ascending — i.e., the
  // natural primary instance for the region.
  const [selectedStpInstance, setSelectedStpInstance] = useState<
    { instanceId: number; instanceType: string } | null
  >(null);

  // Recent-topology-change glow needs to fade smoothly even when the
  // overlay data hasn't been refetched. This tick increments every
  // 30s while STP layer is active, sits in the layer-effect dep
  // array, so the renderer re-runs and recomputes glow alphas from
  // current wall-clock without waiting for a server poll. When STP
  // isn't the active layer there's nothing to decay so we leave the
  // interval off — cheap, but worth doing.
  const [tcGlowTick, setTcGlowTick] = useState(0);
  useEffect(() => {
    if (activeLayer !== 'stp') return;
    const id = setInterval(() => { setTcGlowTick((t) => t + 1); }, 30_000);
    return () => { clearInterval(id); };
  }, [activeLayer]);

  // STP overlay data — one fetch per region, filtered client-side
  // when the instance selector changes.
  const { data: stpOverlay } = useStpOverlay(
    activeLayer === 'stp' ? selectedRegionId : undefined
  );

  // Findings overlay — on by default whenever a region is selected, but
  // gated off entirely (fetch included) when the operator toggles the
  // `findings` overlay off. Passing `undefined` for the region id when the
  // overlay is off disables the query the same way the STP fetch above is
  // gated on `activeLayer === 'stp'`. The badge layer sits above whatever's
  // currently drawn so an operator can see active incidents while reading
  // topology, VLAN, STP, etc.
  const { data: topologyFindings } = useTopologyFindings(
    findingsOverlayOn ? selectedRegionId : undefined
  );
  const findingsByDevice = useMemo(
    () => summarizeFindingsByDevice(topologyFindings?.findings ?? []),
    [topologyFindings]
  );

  // Auto-select the natural-default instance when the overlay data
  // first arrives (or when the region changes). The backend returns
  // `instances` already sorted most-reported-first, then by
  // instance_id — so the first row is the right default. We have
  // to compare on BOTH instanceId and instanceType because the
  // same numeric id appears under multiple variants.
  // During render: each branch only sets when the selection is not already right,
  // so it settles in one extra pass.
  if (stpOverlay) {
    const first = stpOverlay.instances.at(0);
    const stillValid =
      selectedStpInstance !== null &&
      stpOverlay.instances.some(
        (i) =>
          i.instanceId === selectedStpInstance.instanceId &&
          i.instanceType === selectedStpInstance.instanceType
      );
    if (!first) {
      if (selectedStpInstance !== null) setSelectedStpInstance(null);
    } else if (!stillValid) {
      setSelectedStpInstance({ instanceId: first.instanceId, instanceType: first.instanceType });
    }
  }

  // Throughput layer settings
  // One range for the page, scoping the graph and every panel under it, so the
  // canvas and the chart below it can never disagree about the window. Read
  // from the URL alongside ?layer= and ?device=.
  const rangeParam = searchParams.get('range');
  const pageRange: ViewsRange = VIEWS_RANGES.includes(rangeParam as ViewsRange) ? (rangeParam as ViewsRange) : 'live';
  const setPageRange = useCallback((next: ViewsRange) => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      if (next === 'live') params.delete('range');
      else params.set('range', next);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Handle layer change with URL update
  const handleLayerChange = useCallback((layer: LayerType) => {
    // Update URL with layer param, preserving other params
    const newParams = new URLSearchParams(searchParams);
    if (layer === 'topology') {
      newParams.delete('layer'); // Don't clutter URL with default
    } else {
      newParams.set('layer', layer);
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Handle overlay toggle with URL update. Overlays are independent
  // toggles (not mutually exclusive like layers), so this reads the
  // CURRENT overlay set from `prev` inside the updater — not from the
  // `activeOverlays` memo — so it composes correctly even if multiple
  // overlay toggles happen in quick succession, and flips just the one
  // overlay while every other URL param passes through untouched.
  const handleOverlayToggle = useCallback((overlay: OverlayType) => {
    setSearchParams((prev) => {
      const current = parseOverlaysParam(prev.get('overlays'));
      const nextOverlays = new Set(current);
      if (nextOverlays.has(overlay)) {
        nextOverlays.delete(overlay);
      } else {
        nextOverlays.add(overlay);
      }

      const next = new URLSearchParams(prev);
      const isDefault =
        nextOverlays.size === DEFAULT_OVERLAYS.size &&
        [...DEFAULT_OVERLAYS].every((id) => nextOverlays.has(id));
      if (isDefault) {
        next.delete('overlays'); // Don't clutter URL when back at defaults
      } else {
        // Empty string is a valid value here (all overlays off).
        next.set('overlays', [...nextOverlays].join(','));
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Fetch throughput metrics when throughput layer is active
  const { data: throughputData, isError: throughputError, isFetching: throughputLoading } = useThroughputMetrics({
    organizationId,
    regionId: selectedRegionId,
    enabled: activeLayer === 'throughput',
    // Colour on this canvas means "losing frames", so the counters that decide
    // it have to be fetched. Without this every link renders healthy by
    // construction, which is the failure the re-encode exists to end.
    includeErrors: true,
    ...graphQueryFor(pageRange),
  });

  // The selected device's topology row (carries VLAN membership etc.)
  const selectedDevice = useMemo(
    () => topologyData?.devices.find((d) => d.id === selectedDeviceId) ?? null,
    [topologyData, selectedDeviceId],
  );

  // Layer-specific context for the device detail panel — computed here
  // where the per-layer overlay data already lives, then handed to the
  // panel so it reflects whatever layer the operator is viewing. Keeps the
  // panel from refetching data the page already has.
  const deviceLayerContext = useMemo<DeviceLayerContext>(() => {
    switch (activeLayer) {
      case 'vlan': {
        const vlans = (selectedDevice?.vlans ?? [])
          .slice()
          .sort((a, b) => a.vlanId - b.vlanId)
          .map((v) => ({
            vlanId: v.vlanId,
            name: v.vlanName ?? `VLAN ${String(v.vlanId)}`,
            isSelected: selectedVlanId === v.vlanId,
          }));
        return { layer: 'vlan', vlans };
      }
      case 'stp': {
        const inst = selectedStpInstance;
        const state =
          inst && stpOverlay
            ? stpOverlay.deviceStates.find(
                (s) =>
                  s.deviceId === selectedDeviceId &&
                  s.instanceId === inst.instanceId &&
                  s.instanceType === inst.instanceType,
              ) ?? null
            : null;
        const instMeta =
          inst && stpOverlay
            ? stpOverlay.instances.find(
                (i) => i.instanceId === inst.instanceId && i.instanceType === inst.instanceType,
              )
            : undefined;
        return {
          layer: 'stp',
          instanceLabel: instMeta ? stpInstanceLabel(instMeta) : '',
          state,
        };
      }
      case 'throughput': {
        const links = (topologyData?.connections ?? [])
          .filter(
            (c) => c.sourceDeviceId === selectedDeviceId || c.targetDeviceId === selectedDeviceId,
          )
          .map((c) => {
            const remoteEp = c.sourceDeviceId === selectedDeviceId ? c.target : c.source;
            const t = throughputData?.connections[c.id];
            return {
              remote: remoteEp.deviceName ?? remoteEp.deviceIp,
              utilization: t ? maxUtilization(t.inbound.utilization, t.outbound.utilization) : null,
              totalBps: t ? t.inbound.current + t.outbound.current : 0,
              hasErrors: t?.errors.hasErrors ?? false,
            };
          })
          // Unknown-speed links sort last rather than as 0%: they are not idle,
          // we simply cannot rank them.
          .sort((a, b) => (b.utilization ?? -1) - (a.utilization ?? -1));
        return { layer: 'throughput', links };
      }
      case 'physical': {
        const links = (topologyData?.connections ?? [])
          .filter(
            (c) => c.sourceDeviceId === selectedDeviceId || c.targetDeviceId === selectedDeviceId,
          )
          .map((c) => {
            const remoteEp = c.sourceDeviceId === selectedDeviceId ? c.target : c.source;
            return {
              remote: remoteEp.deviceName ?? remoteEp.deviceIp,
              speed: speedBpsToCategory(c.speedBps),
            };
          });
        return { layer: 'physical', links };
      }
      case 'topology':
      case 'ospf':
        return { layer: activeLayer };
    }
  }, [
    activeLayer,
    selectedDevice,
    selectedDeviceId,
    selectedVlanId,
    selectedStpInstance,
    stpOverlay,
    throughputData,
    topologyData,
  ]);

  // Track current layer cleanup function
  const layerCleanupRef = useRef<{ remove: () => void } | null>(null);

  // Apply throughput data to edges when available
  useEffect(() => {
    if (activeLayer !== 'throughput') return;

    const cy = liveCy;
    if (!cy) return;

    // Apply throughput data to edges
    cy.edges().forEach((edge) => {
      const edgeId = edge.id();
      const edgeThroughput = throughputData?.connections[edgeId];
      if (edgeThroughput) {
        edge.data('throughput', edgeThroughput);
      } else {
        edge.removeData('throughput');
      }
    });

    // Update layer visualization to reflect new data
    const layers = graphRef.current?.getLayers();
    if (layers && layerCleanupRef.current) {
      layerCleanupRef.current.remove();
      layerCleanupRef.current = applyLayer(cy, layers, activeLayer, {});
    }
    // `elements` is a dependency because a live topology update rebuilds the
    // graph wholesale (NetworkGraph's elements effect does
    // `cy.elements().remove()` then `cy.add()`), which throws away the
    // `throughput` field this effect writes onto each edge. Without it the
    // Traffic lens survives exactly until the first websocket device update,
    // then every link falls back to the muted "no data" stroke while the
    // Lossiest panel — reading `throughputData` directly — still shows rates.
    //
    // Ordering note: React flushes every passive cleanup before any body, so
    // the layer effect below has already nulled `layerCleanupRef` by the time
    // this runs on an `elements` change. This effect therefore re-attaches the
    // data and skips its own re-apply, and the layer effect builds the canvas
    // against populated edges. No double teardown.
  }, [throughputData, activeLayer, liveCy, elements]);

  // Apply layer visualization when activeLayer or selectedVlanId changes
  useEffect(() => {
    // Clean up previous layer
    if (layerCleanupRef.current) {
      layerCleanupRef.current.remove();
      layerCleanupRef.current = null;
    }

    // Apply new layer if graph is ready. `liveCy` (reactive) rather than an
    // imperative ref-read — see the liveCy declaration for the StrictMode
    // instance-swap hazard this avoids. The layers plugin is re-read from
    // the ref, which is safe because this effect re-runs on liveCy changes.
    const cy = liveCy;
    const layers = graphRef.current?.getLayers();

    if (cy && layers && activeLayer !== 'topology') {
      layerCleanupRef.current = applyLayer(cy, layers, activeLayer, {
        selectedVlanId: activeLayer === 'vlan' ? selectedVlanId : null,
        // `stpOverlay` already shapes its deviceStates / edgeStates
        // to the renderer's expected types, so a structural pass-
        // through is enough — TypeScript's structural typing means
        // the lastTopologyChange field carries through to the
        // TC-glow logic without an explicit projection.
        stpOverlay: activeLayer === 'stp' && stpOverlay
          ? {
              deviceStates: stpOverlay.deviceStates,
              edgeStates: stpOverlay.edgeStates,
            }
          : undefined,
        selectedStpInstance: activeLayer === 'stp' ? selectedStpInstance : null,
        // Selected device drives single-device mode automatically — no
        // separate UI toggle. The existing selection is the trigger.
        stpFocusDeviceId: activeLayer === 'stp' ? selectedDeviceId : null,
      });
    }

    // Cleanup on unmount
    return () => {
      if (layerCleanupRef.current) {
        layerCleanupRef.current.remove();
        layerCleanupRef.current = null;
      }
    };
  }, [activeLayer, elements, selectedVlanId, stpOverlay, selectedStpInstance, selectedDeviceId, tcGlowTick, liveCy]);

  // Track findings overlay layer cleanup (independent of the active
  // visualization layer; gated on the `findings` overlay toggle instead).
  const findingsLayerCleanupRef = useRef<{ remove: () => void } | null>(null);

  // Re-mount the findings badge layer whenever the underlying
  // summaries change. Cytoscape's `renderPerNode` reads the closure
  // it was given at mount time, so updating data means tearing
  // down and re-applying — same pattern as the other layers.
  useEffect(() => {
    if (findingsLayerCleanupRef.current) {
      findingsLayerCleanupRef.current.remove();
      findingsLayerCleanupRef.current = null;
    }

    // Overlay off — render nothing (the fetch itself is also disabled
    // above, so `findingsByDevice` is already empty by this point, but
    // this early return keeps the "off" behavior explicit and independent
    // of that fetch-gating detail).
    if (!findingsOverlayOn) return;

    const cy = liveCy;
    const layers = graphRef.current?.getLayers();
    if (!cy || !layers) return;

    findingsLayerCleanupRef.current = renderFindingsLayer(
      cy,
      layers,
      findingsByDevice,
      severityColor,
      severityNeedsDarkText
    );

    return () => {
      if (findingsLayerCleanupRef.current) {
        findingsLayerCleanupRef.current.remove();
        findingsLayerCleanupRef.current = null;
      }
    };
    // `elements` is in the dep array so the badge layer re-mounts
    // after the graph is rebuilt (e.g. region change) — without
    // that, the canvas would be torn down by the graph rebuild
    // but the cleanup ref would still point at a stale layer.
  }, [findingsByDevice, elements, findingsOverlayOn, liveCy]);

  // Path overlay (S0): same teardown/re-mount lifecycle as the findings
  // layer — see that effect's comments for why `elements` and `liveCy`
  // are in the dep array.
  const pathLayerCleanupRef = useRef<{ remove: () => void } | null>(null);
  useEffect(() => {
    if (pathLayerCleanupRef.current) {
      pathLayerCleanupRef.current.remove();
      pathLayerCleanupRef.current = null;
    }
    if (!pathOverlayOn || pathDeviceIds.length === 0) return;
    const cy = liveCy;
    const layers = graphRef.current?.getLayers();
    if (!cy || !layers) return;
    pathLayerCleanupRef.current = renderPathLayer(cy, layers, pathDeviceIds);
    return () => {
      if (pathLayerCleanupRef.current) {
        pathLayerCleanupRef.current.remove();
        pathLayerCleanupRef.current = null;
      }
    };
  }, [pathDeviceIds, elements, pathOverlayOn, liveCy]);

  // Track VLAN hull layer cleanup
  const vlanHullCleanupRef = useRef<{ remove: () => void } | null>(null);

  // Draw convex hull for selected VLAN
  useEffect(() => {
    // Clean up previous hull
    if (vlanHullCleanupRef.current) {
      vlanHullCleanupRef.current.remove();
      vlanHullCleanupRef.current = null;
    }

    // Only draw hull when VLAN layer is active and a VLAN is selected
    if (activeLayer !== 'vlan' || selectedVlanId === null) return;

    const cy = liveCy;
    const layers = graphRef.current?.getLayers();
    if (!cy || !layers) return;

    // Create a canvas layer for the hull
    const hullCanvas = layers.nodeLayer.insertBefore('canvas');

    // Draw the hull dynamically - recomputes positions each render for drag support
    const drawHull = (ctx: CanvasRenderingContext2D) => {
      // Find all nodes that have the selected VLAN (query fresh each render)
      const nodesWithVlan = cy.nodes().filter((node) => {
        const vlans = node.data('vlans') as Array<{ vlanId: number }> | undefined;
        return vlans?.some((v) => v.vlanId === selectedVlanId) ?? false;
      });

      if (nodesWithVlan.length === 0) return;

      const color = getVlanColor(selectedVlanId);
      const padding = 55;

      // Get current node positions
      const points: Array<{ x: number; y: number }> = [];
      nodesWithVlan.forEach((node) => {
        const pos = node.position();
        points.push({ x: pos.x, y: pos.y });
      });

      ctx.fillStyle = color + '20'; // ~12% opacity
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);

      if (points.length === 1) {
        // Single node: draw a circle around it
        ctx.beginPath();
        ctx.arc(points[0].x, points[0].y, padding, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      // } else if (points.length === 2) {
      //   // Two nodes: draw a capsule/stadium shape
      //   const p1 = points[0];
      //   const p2 = points[1];
      //   const dx = p2.x - p1.x;
      //   const dy = p2.y - p1.y;

      //   // Draw capsule: two semicircles connected by lines
      //   const angle = Math.atan2(dy, dx);

      //   ctx.beginPath();
      //   // Semicircle around p1 (the side facing away from p2)
      //   // Goes from bottom-perpendicular, around the back (away from p2), to top-perpendicular
      //   ctx.arc(p1.x, p1.y, padding, angle + Math.PI / 2, angle - Math.PI / 2, false);
      //   // Canvas auto-draws line to start of next arc (top connecting line)
      //   // Semicircle around p2 (the side facing away from p1)
      //   // Goes from top-perpendicular, around the back (away from p1), to bottom-perpendicular
      //   ctx.arc(p2.x, p2.y, padding, angle - Math.PI / 2, angle + Math.PI / 2, false);
      //   // closePath draws bottom connecting line
      //   ctx.closePath();
      //   ctx.fill();
      //   ctx.stroke();
      } else {
        // 3+ nodes: draw blob shape that excludes non-VLAN nodes
        // Build a single path to avoid overlapping fill accumulation

        // Build complete fill path (circles + capsules) as one path
        ctx.beginPath();

        // Add circles for each node that has the VLAN in its device VLAN table
        points.forEach((p) => {
          ctx.moveTo(p.x + padding, p.y);
          ctx.arc(p.x, p.y, padding, 0, 2 * Math.PI);
        });

        // Add capsules for edges where EITHER interface carries this VLAN
        // (the traffic is on the link even if the other device doesn't "know" the VLAN)
        cy.edges().forEach((edge) => {
          const edgeVlans = edge.data('vlans') as number[] | undefined;
          if (edgeVlans?.includes(selectedVlanId)) {
            const p1 = edge.source().position();
            const p2 = edge.target().position();
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const angle = Math.atan2(dy, dx);

            // Add capsule to path
            ctx.moveTo(
              p1.x + padding * Math.cos(angle + Math.PI / 2),
              p1.y + padding * Math.sin(angle + Math.PI / 2)
            );
            ctx.arc(p1.x, p1.y, padding, angle + Math.PI / 2, angle - Math.PI / 2, false);
            ctx.arc(p2.x, p2.y, padding, angle - Math.PI / 2, angle + Math.PI / 2, false);
            ctx.closePath();
          }
        });

        // Single fill for entire path - no overlap accumulation
        ctx.fill();

        // Stroke individual circles for dashed outline
        points.forEach((p) => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, padding, 0, 2 * Math.PI);
          ctx.stroke();
        });
      }
      ctx.setLineDash([]);
    };

    // Register the draw callback - updateOnRender ensures it redraws when graph changes
    hullCanvas.callback(drawHull);
    hullCanvas.updateOnRender = true;

    vlanHullCleanupRef.current = {
      remove: () => {
        // Order matters. `updateOnRender = true` above registered
        // `cy.on('render', layer.update)`, and cytoscape-layers'
        // `CanvasBaseLayer.remove()` only does `node.remove()` — it never
        // unregisters that handler. Dropping the layer without clearing the
        // flag first leaves a listener redrawing a DETACHED full-size canvas
        // on every frame, for the life of the graph, once per hull remount
        // (this effect re-runs on every VLAN selection). Setting it false
        // calls `cy.off('render', ...)` with the same stable `update`
        // reference, which is the only way to take it back off.
        hullCanvas.updateOnRender = false;
        hullCanvas.remove();
      },
    };

    return () => {
      if (vlanHullCleanupRef.current) {
        vlanHullCleanupRef.current.remove();
        vlanHullCleanupRef.current = null;
      }
    };
  }, [activeLayer, selectedVlanId, elements, liveCy]);

  // Clear selected VLAN when switching away from VLAN layer
  if (activeLayer !== 'vlan' && selectedVlanId !== null) {
    setSelectedVlanId(null);
  }

  // Track controls modal visibility
  const [showControls, setShowControls] = useState<boolean>(false);
  const [showResync, setShowResync] = useState<boolean>(false);

  // An endpoint selection lives in the URL like the others: `endpoint` alone opens the endpoint's
  // panel, and with `endpointLink` one of its links.
  const selectedEndpointId = searchParams.get('endpoint');
  const selectedEndpointLinkId = searchParams.get('endpointLink');
  const selectEndpoint = useCallback((endpointId: string, linkId: string | null) => {
    setSelectedDeviceId(null);
    setSelectedLinkData(null);
    setHighlightedInterfaceIds([]);
    setSelectedUnconnectedInterface(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('device');
      next.delete('link');
      next.delete('interface');
      next.delete('unconnected');
      next.set('endpoint', endpointId);
      if (linkId) next.set('endpointLink', linkId);
      else next.delete('endpointLink');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Handle node click to show device details — or, for endpoints-overlay
  // satellite nodes, the endpoint's panel.
  const handleNodeClick = useCallback((nodeId: string, nodeData: unknown) => {
    const data = nodeData as {
      isEndpoint?: boolean;
      isEndpointOverflow?: boolean;
      parentDeviceId?: string;
    };

    if (data.isEndpoint) {
      // The cy/data id is `endpoint:{id}` (required so the satellite edge's
      // `source` can reference it) — strip the prefix to get the endpoint's id.
      selectEndpoint(nodeId.replace(/^endpoint:/, ''), null);
      return;
    }
    if (data.isEndpointOverflow) {
      // There's no "endpoints" tab/route on the device details page today
      // (DeviceEndpointsTable exists but isn't wired into any route or
      // tab), so this is intentionally a no-op rather than a guessed URL.
      return;
    }

    setSelectedDeviceId(nodeId);
    setSelectedLinkData(null); // Clear link selection when selecting a device
    setHighlightedInterfaceIds([]); // Clear interface highlight
    setSelectedUnconnectedInterface(null); // Clear unconnected interface
    // Mutate the URL in-place: drop competing selection params but
    // preserve everything else (specifically `?layer=...`). The old
    // object-form `setSearchParams({device: nodeId})` replaced ALL
    // params, which silently dropped the active layer choice and
    // made clicking a node feel like it reset the visualization.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('device', nodeId);
      next.delete('link');
      next.delete('interface');
      next.delete('unconnected');
      next.delete('endpoint');
      next.delete('endpointLink');
      return next;
    }, { replace: true });
  }, [setSearchParams, selectEndpoint]);

  // Handle edge click to show link details
  const handleEdgeClick = useCallback((_edgeId: string, edgeData: unknown) => {
    const data = edgeData as {
      connectionType: string;
      confidenceScore: number;
      sourceEndpoint: ConnectionEndpoint;
      targetEndpoint: ConnectionEndpoint;
      physical?: { speed: string; speedBps?: number };
      endpointLink?: string;
      endpointId?: string;
      linkId?: string;
    };
    // An endpoint's satellite edge is its attachment: it opens as that endpoint's link. The
    // overflow edge stands for several endpoints, so it opens nothing.
    if (data.endpointLink) {
      if (data.endpointLink !== 'aggregate' && data.endpointId && data.linkId) {
        selectEndpoint(data.endpointId, data.linkId);
      }
      return;
    }
    setSelectedLinkData({
      id: _edgeId,
      connectionType: data.connectionType,
      confidenceScore: data.confidenceScore,
      sourceEndpoint: data.sourceEndpoint,
      targetEndpoint: data.targetEndpoint,
      physical: data.physical,
    });
    setSelectedDeviceId(null); // Clear device selection when selecting a link
    setHighlightedInterfaceIds([]); // Clear interface highlight
    setSelectedUnconnectedInterface(null); // Clear unconnected interface
    // Preserve `layer` and other unrelated params; only swap which
    // selection (link vs. device vs. interface) is currently active.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('link', _edgeId);
      next.delete('device');
      next.delete('interface');
      next.delete('unconnected');
      next.delete('endpoint');
      next.delete('endpointLink');
      return next;
    }, { replace: true });
  }, [setSearchParams, selectEndpoint]);

  // Handle device selection from link detail panel (clicking an endpoint)
  // Just pass the main interface ID - DeviceDetailPanel will expand to include LAG members
  const handleLinkDeviceSelect = useCallback((endpoint: ConnectionEndpoint) => {
    setSelectedDeviceId(endpoint.deviceId);
    setSelectedLinkData(null); // Clear link selection
    setSelectedUnconnectedInterface(null); // Clear unconnected interface
    // Set the main interface to highlight (LAG member expansion happens in DeviceDetailPanel)
    setHighlightedInterfaceIds(endpoint.interfaceId ? [endpoint.interfaceId] : []);

    // Push to history (creates new entry so back button returns to
    // link view). Preserve unrelated params so the user doesn't lose
    // their active layer on this navigation either.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('device', endpoint.deviceId);
      next.delete('link');
      next.delete('unconnected');
      next.delete('endpoint');
      next.delete('endpointLink');
      if (endpoint.interfaceId) {
        next.set('interface', endpoint.interfaceId);
      } else {
        next.delete('interface');
      }
      return next;
    });
  }, [setSearchParams]);

  // Helper: strip every selection-related param at once while
  // preserving anything else (notably `layer`). Used by all three
  // "close panel" handlers and by the background-click handler so
  // they share semantics.
  const clearSelectionParams = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('device');
      next.delete('link');
      next.delete('interface');
      next.delete('unconnected');
      next.delete('endpoint');
      next.delete('endpointLink');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Handle closing device detail panel
  const handleCloseDevicePanel = useCallback(() => {
    setSelectedDeviceId(null);
    setHighlightedInterfaceIds([]);
    setSelectedUnconnectedInterface(null);
    clearSelectionParams();
  }, [clearSelectionParams]);

  // Handle closing link detail panel
  const handleCloseLinkPanel = useCallback(() => {
    setSelectedLinkData(null);
    setSelectedUnconnectedInterface(null);
    clearSelectionParams();
  }, [clearSelectionParams]);

  // Handle closing unconnected interface detail panel
  const handleCloseInterfacePanel = useCallback(() => {
    setSelectedUnconnectedInterface(null);
    clearSelectionParams();
  }, [clearSelectionParams]);

  // Handle "back to device" from interface detail panel
  const handleBackToDevice = useCallback(() => {
    if (selectedUnconnectedInterface) {
      setSelectedDeviceId(selectedUnconnectedInterface.deviceId);
      setHighlightedInterfaceIds([selectedUnconnectedInterface.interfaceId]);
      setSelectedUnconnectedInterface(null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('device', selectedUnconnectedInterface.deviceId);
        next.set('interface', selectedUnconnectedInterface.interfaceId);
        next.delete('link');
        next.delete('unconnected');
        next.delete('endpoint');
        next.delete('endpointLink');
        return next;
      });
    }
  }, [selectedUnconnectedInterface, setSearchParams]);

  // Handle interface click from device detail panel - finds and shows the link for that interface
  const handleInterfaceClick = useCallback((deviceId: string, interfaceId: string) => {
    const cy = graphRef.current?.getCy();
    if (!cy) return;

    // Search all edges to find one where this device/interface is an endpoint
    const matchingEdge = cy.edges().filter((edge) => {
      const data = edge.data() as {
        sourceEndpoint?: ConnectionEndpoint;
        targetEndpoint?: ConnectionEndpoint;
      };
      // Check if either endpoint matches
      const sourceMatches = data.sourceEndpoint?.deviceId === deviceId &&
        data.sourceEndpoint.interfaceId === interfaceId;
      const targetMatches = data.targetEndpoint?.deviceId === deviceId &&
        data.targetEndpoint.interfaceId === interfaceId;
      return sourceMatches || targetMatches;
    }).first();

    if (matchingEdge.length > 0) {
      const edgeId = matchingEdge.id();
      const data = matchingEdge.data() as {
        connectionType: string;
        confidenceScore: number;
        sourceEndpoint: ConnectionEndpoint;
        targetEndpoint: ConnectionEndpoint;
        physical?: { speed: string; speedBps?: number };
      };

      // Show link details
      setSelectedLinkData({
        id: edgeId,
        connectionType: data.connectionType,
        confidenceScore: data.confidenceScore,
        sourceEndpoint: data.sourceEndpoint,
        targetEndpoint: data.targetEndpoint,
        physical: data.physical,
      });
      setSelectedDeviceId(null);
      setHighlightedInterfaceIds([]);
      setSelectedUnconnectedInterface(null);

      // Push to history so back button returns to device view.
      // Preserve the layer param so the user keeps their active
      // visualization across this navigation.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('link', edgeId);
        next.delete('device');
        next.delete('interface');
        next.delete('unconnected');
        next.delete('endpoint');
        next.delete('endpointLink');
        return next;
      });
    } else {
      // No link found - show interface details panel for unconnected port
      setSelectedUnconnectedInterface({ deviceId, interfaceId });
      setSelectedLinkData(null);
      setSelectedDeviceId(null);
      setHighlightedInterfaceIds([]);

      // Push to history with interface params, preserving layer.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('device', deviceId);
        next.set('interface', interfaceId);
        next.set('unconnected', 'true');
        next.delete('endpoint');
        next.delete('endpointLink');
        next.delete('link');
        return next;
      });
    }
  }, [setSearchParams]);

  // Handle node drag end: snapshot all positions and pin the dragged node.
  // Endpoint/overflow satellites are the exception: their drops are
  // deliberately ephemeral (not persisted, not pinned — the next parent
  // move or layout re-rings them), and letting one through here would
  // write an `endpoint:*` id into the device pin set.
  const handleNodeDragEnd = useCallback((nodeId: string) => {
    const cy = graphRef.current?.getCy();
    if (!cy) return;
    const node = cy.getElementById(nodeId);
    if (node.length > 0 && (node.data('isEndpoint') || node.data('isEndpointOverflow'))) return;
    savePositions(cy, nodeIds, nodeId);
  }, [savePositions, nodeIds]);

  // Reset positions: drop cached positions and pins, then re-run a fresh
  // (randomized, fit) auto-layout so the graph doesn't sit empty/collapsed
  // until the next unrelated re-render happens to trigger the layout effect.
  const handleResetPositions = useCallback(() => {
    clearCachedPositions();
    autoLayoutKeyRef.current = '';
    const cy = graphRef.current?.getCy();
    if (cy) runAutoLayout(cy, [], true);
  }, [clearCachedPositions, runAutoLayout]);

  // Auto-arrange: re-layout everything from scratch but KEEP pinned nodes in
  // place (unlike Reset, which unpins). Reads the pin set fresh from storage
  // rather than the memoized `placement` — a drag saves its pin without a
  // re-render, so the memo can be stale by the time the button is clicked.
  const handleAutoArrange = useCallback(() => {
    const cy = graphRef.current?.getCy();
    if (!cy) return;
    const current = getPlacement(nodeIds);
    runAutoLayout(cy, current.pinnedIds, true);
  }, [getPlacement, nodeIds, runAutoLayout]);

  return (
    <PageContainer>
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Views</h1>
            <select
              value={selectedRegionId || ''}
              onChange={(e) => { setSelectedRegionId(e.target.value); }}
              disabled={regionsLoading || !regions || regions.length === 0}
              className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              {!regions || regions.length === 0 ? (
                <option value="">No regions available</option>
              ) : (
                regions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))
              )}
            </select>
          </div>
          {/* One range for the page, in the filter row above everything it
              scopes — the graph, the rail and whichever panel is open. It used
              to be a floating chip group over the canvas that appeared only
              while the traffic lens was active, which meant the panel below
              could be showing an hour while the edges showed a week. */}
          <div className="inline-flex rounded-md border border-input overflow-hidden" role="group" aria-label="Time range">
            {VIEWS_RANGES.map((range) => (
              <button
                key={range}
                type="button"
                aria-pressed={pageRange === range}
                className={cn(
                  'px-2.5 py-1.5 text-sm border-r border-input last:border-r-0',
                  pageRange === range ? 'bg-sev-5/15 text-sev-5 font-medium' : 'bg-background hover:bg-accent',
                )}
                onClick={() => { setPageRange(range); }}
                title={range === 'live' ? 'The latest reading, refreshed every 30 seconds' : `The last ${range}`}
              >
                {viewsRangeLabel(range)}
              </button>
            ))}
          </div>
        </div>

        {/* What the graph draws gets its own row. The layers and overlays used
            to share the row above with the region and range, and that row did
            not wrap: on a laptop-width screen the last layers and the overlays
            ran off the right edge with no way to reach them. Each group scrolls
            sideways when it is wider than the screen on its own. */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {/* Lens tabs — mutually exclusive base layer for the graph. */}
          <div className="flex min-w-0 max-w-full items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Layer</span>
            <div className="inline-flex min-w-0 rounded-md border border-input overflow-x-auto" role="group" aria-label="Layer">
              {(Object.keys(LAYER_CONFIGS) as LayerType[]).map((layerType) => {
                const config = LAYER_CONFIGS[layerType];
                const isActive = activeLayer === layerType;
                return (
                  <button
                    key={layerType}
                    type="button"
                    aria-pressed={isActive}
                    className={cn(
                      'shrink-0 px-3 py-1.5 text-sm border-r border-input last:border-r-0 disabled:opacity-50 disabled:cursor-not-allowed',
                      isActive ? 'bg-sev-5/15 text-sev-5 font-medium' : 'bg-background hover:bg-accent',
                    )}
                    onClick={() => { handleLayerChange(layerType); }}
                    disabled={!config.enabled}
                    title={config.description}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Overlay chips — independent, stackable toggles drawn on top of
              whichever lens is active. Visually distinct group (own border,
              separated by the parent's gap) but reuses the lens tabs' active/
              inactive classes so it reads as the same control family. */}
          <div className="flex min-w-0 max-w-full items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Overlays</span>
            <div className="inline-flex min-w-0 rounded-md border border-input overflow-x-auto" role="group" aria-label="Overlays">
              {(Object.keys(OVERLAY_CONFIGS) as OverlayType[]).filter((id) => !OVERLAY_CONFIGS[id].hidden).map((overlayType) => {
                const config = OVERLAY_CONFIGS[overlayType];
                const isActive = activeOverlays.has(overlayType);
                return (
                  <button
                    key={overlayType}
                    type="button"
                    aria-pressed={isActive}
                    className={cn(
                      'shrink-0 px-3 py-1.5 text-sm border-r border-input last:border-r-0',
                      isActive ? 'bg-sev-5/15 text-sev-5 font-medium' : 'bg-background hover:bg-accent',
                    )}
                    onClick={() => { handleOverlayToggle(overlayType); }}
                    title={config.description}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* About-this-view info. The former always-on explanatory banner ate
              the top third of the graph; tuck the same guidance behind a compact
              info chip that matches the control row's height/border. */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                aria-label="About this view"
                title="About this view"
              >
                <Info className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 text-sm">
              <p className="font-medium mb-1">Network topology view</p>
              <p className="text-muted-foreground text-xs">
                Real network devices and connections for the selected region.
              </p>
              <p className="text-muted-foreground text-xs mt-2">
                <strong className="text-foreground">Layers</strong> switch what the graph
                shows: VLAN maps network segmentation, Traffic draws each link as
                thick as it is busy and colours the ones losing frames, and OSPF
                shows routing areas and link costs. Overlays stack on top of the
                active layer.
              </p>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {topologyError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Error loading topology:</strong>{' '}
            {topologyError instanceof Error ? topologyError.message : 'Unknown error occurred'}
          </AlertDescription>
        </Alert>
      ) : null}

      {topologyLoading ? (
        <Alert className="mb-4 border-sev-5/30 bg-sev-5/5">
          <AlertDescription>
            <p>Loading network topology data…</p>
            <Progress value={undefined} className="h-1 mt-2" />
          </AlertDescription>
        </Alert>
      ) : null}

      {!topologyLoading && !topologyError ? (
        <div className="relative overflow-hidden rounded-lg border border-border bg-card">
          <button
            type="button"
            className="absolute top-4 left-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-sev-5 text-white font-bold text-lg shadow-md hover:bg-sev-5/90"
            onClick={() => { setShowControls(!showControls); }}
            title="Graph controls"
          >
            ?
          </button>

          {elements.length > 0 ? (
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="shadow-md"
                onClick={() => { setShowResync(true); }}
                title="Have every gateway re-state the links it holds, and drop the ones it no longer reports"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Re-audit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="shadow-md"
                onClick={handleAutoArrange}
                title="Re-arrange devices automatically (pinned devices stay put)"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                Auto-arrange
              </Button>
            </div>
          ) : null}

          {showControls ? (
            <div className="absolute top-16 left-4 z-10 min-w-[250px] max-w-xs rounded-lg border border-border bg-card p-4 shadow-md">
              <button
                type="button"
                className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
                onClick={() => { setShowControls(false); }}
                aria-label="Close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <h3 className="text-sm font-semibold mb-2">Graph controls</h3>
              <div className="text-sm space-y-2">
                <p><strong>Mouse controls:</strong></p>
                <ul className="list-disc pl-5 text-xs space-y-0.5">
                  <li>Click and drag to pan</li>
                  <li>Scroll to zoom in/out</li>
                  <li>Click nodes to view details</li>
                  <li>Drag nodes to reposition</li>
                </ul>
                <p className="pt-1"><strong>Icon theme:</strong></p>
                <select
                  value={iconThemeId}
                  onChange={(e) => { handleThemeChange(e.target.value); }}
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {availableThemes.map((theme) => (
                    <option key={theme.id} value={theme.id}>
                      {theme.name}
                    </option>
                  ))}
                </select>
                <p className="pt-1"><strong>Layout:</strong></p>
                <p className="text-xs text-muted-foreground">
                  Devices are arranged automatically. Drag a device to pin it in place —
                  Auto-arrange re-arranges everything else around pinned devices, while
                  Reset layout unpins everything and starts fresh.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sev-3 hover:text-sev-3"
                  onClick={handleResetPositions}
                >
                  Reset layout
                </Button>
              </div>
            </div>
          ) : null}

          {elements.length === 0 ? (
            <Alert className="m-4 border-sev-3/40 bg-sev-3/10">
              <AlertDescription>
                No devices found in the selected region. Add devices to see the network topology.
              </AlertDescription>
            </Alert>
          ) : (
              <NetworkGraph
                // Remount per region so the constructor's initial fit frames each
                // region's saved coordinates — in-session element updates no
                // longer re-fit (see NetworkGraph.tsx's elements effect).
                key={selectedRegionId ?? 'none'}
                ref={graphRef}
                onCyChanged={setLiveCy}
                elements={elements}
                style={graphStyle}
                theme={resolvedTheme}
                layout={graphLayout}
                onNodeClick={handleNodeClick}
                onEdgeClick={handleEdgeClick}
                onBackgroundClick={clearSelectionParams}
                onNodeDragEnd={handleNodeDragEnd}
                selectedNodeId={selectedDeviceId ?? (selectedEndpointId ? `endpoint:${selectedEndpointId}` : null)}
                selectedEdgeId={
                  selectedLinkData?.id ??
                  (selectedEndpointId && selectedEndpointLinkId ? `endpoint-edge:${selectedEndpointId}` : undefined)
                }
              />
            )}

            {/* Throughput Layer Loading Spinner */}
            {activeLayer === 'throughput' && throughputLoading && !throughputError && (
              <div
                className="absolute top-4 right-4 z-10 rounded-md border border-border bg-popover/95 text-popover-foreground shadow-md"
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading metrics…</span>
              </div>
            )}

            {/* Throughput Layer Error Badge */}
            {activeLayer === 'throughput' && throughputError && (
              <div
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  zIndex: 10,
                  backgroundColor: '#f14668',
                  color: 'white',
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontWeight: 600 }}>Metrics unavailable</span>
              </div>
            )}

            {/* Which link in this view is worst — a whole-view comparison the
                panels below structurally cannot make, and the keyboard route to
                values the canvas only offers on hover. */}
            {activeLayer === 'throughput' && topologyData ? (
              <TopTalkersRail
                connections={topologyData.connections}
                throughput={throughputData?.connections}
                selectedId={selectedLinkData?.id}
                onSelect={handleRailSelect}
              />
            ) : null}

            {/* Traffic Layer Legend */}
            {activeLayer === 'throughput' && (
              /* Bottom-left, because the ranked rail owns the right-hand side of
                 this lens, and two floating cards fighting for one corner is how
                 a legend ends up unreadable. */
              <div className="absolute bottom-4 left-4 z-10 rounded-md border border-border bg-popover/95 text-popover-foreground shadow-md px-4 py-3 text-[0.85rem]">
                <div className="font-semibold mb-2">Traffic</div>
                <div className="flex flex-col gap-1.5">
                  {/* Width is the only quantitative channel here, and it needs
                      no denominator — which is why it carries magnitude. */}
                  <div className="flex items-center gap-2">
                    <span className="inline-flex flex-col justify-center gap-1 w-6">
                      <span className="block rounded-full bg-fg-subtle" style={{ height: '2.5px' }} />
                      <span className="block rounded-full bg-fg-subtle" style={{ height: '9px' }} />
                    </span>
                    <span>Thicker = more bits/sec</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-6 rounded-full bg-sev-1" style={{ height: '4px' }} />
                    <span>Losing frames, or at line rate</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Droplet className="w-3.5 h-3.5 text-sev-1" aria-hidden="true" />
                    <span>Errors or discards</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Gauge className="w-3.5 h-3.5 text-sev-1" aria-hidden="true" />
                    <span>At or over line rate</span>
                  </div>
                  <p className="text-muted-foreground max-w-[15rem] mt-1">
                    Nothing is coloured for being healthy — no mark means no fault counted.
                  </p>
                </div>
              </div>
            )}

            {/* Physical Layer Legend */}
            {activeLayer === 'physical' && (
              <div
                className="absolute bottom-4 right-4 z-10 rounded-md border border-border bg-popover/95 text-popover-foreground shadow-md"
                style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.85rem',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Link Speed</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '24px', height: '4px', backgroundColor: '#C0392B', borderRadius: '2px' }} />
                    <span>10 Mbps</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '24px', height: '4px', backgroundColor: '#E67E22', borderRadius: '2px' }} />
                    <span>100 Mbps</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '24px', height: '4px', backgroundColor: '#16A085', borderRadius: '2px' }} />
                    <span>1 Gbps</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '24px', height: '4px', backgroundColor: '#6C5CE7', borderRadius: '2px' }} />
                    <span>10 Gbps</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '24px', height: '4px', backgroundColor: '#95A5A6', borderRadius: '2px' }} />
                    <span>Unknown</span>
                  </div>
                </div>

                {/* Wireless band swatches — only meaningful when the endpoints
                    overlay is on (otherwise there are no wireless endpoint
                    edges in the graph to color). */}
                {endpointsOverlayOn && (
                  <>
                    <div className="border-t border-border" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                      Wireless
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      {(['2.4GHz', '5GHz', '6GHz'] as const).map((band) => (
                        <div key={band} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: '24px',
                              height: '2px',
                              background: `repeating-linear-gradient(90deg, ${getWirelessBandColor(band) ?? '#95A5A6'} 0px, ${getWirelessBandColor(band) ?? '#95A5A6'} 5px, transparent 5px, transparent 9px)`,
                            }}
                          />
                          <span>{band.replace('GHz', ' GHz')}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className="text-muted-foreground" style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                  Hover links for details
                </div>
              </div>
            )}

            {/* VLAN Layer Legend */}
            {activeLayer === 'vlan' && uniqueVlans.length > 0 && (
              <div
                className="absolute bottom-4 right-4 z-10 rounded-md border border-border bg-popover/95 text-popover-foreground shadow-md"
                style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.85rem',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  minWidth: '180px',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>VLANs</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {uniqueVlans.map((vlan) => (
                    <button
                      key={vlan.vlanId}
                      onClick={() => {
                        setSelectedVlanId(selectedVlanId === vlan.vlanId ? null : vlan.vlanId);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.35rem 0.5rem',
                        border: selectedVlanId === vlan.vlanId ? `2px solid ${getVlanColor(vlan.vlanId)}` : '2px solid transparent',
                        borderRadius: '4px',
                        backgroundColor: selectedVlanId === vlan.vlanId ? getVlanColor(vlan.vlanId) + '15' : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: getVlanColor(vlan.vlanId),
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontWeight: selectedVlanId === vlan.vlanId ? 600 : 400 }}>
                        {vlan.displayName}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="border-t border-border text-muted-foreground" style={{ marginTop: '0.75rem', paddingTop: '0.5rem', fontSize: '0.75rem' }}>
                  {selectedVlanId === null ? (
                    <>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '2px', background: '#7a7a7a', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Access (single VLAN)
                      </div>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '3px', background: 'repeating-linear-gradient(90deg, #7a7a7a 0px, #7a7a7a 5px, transparent 5px, transparent 9px)', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Trunk (tagged)
                      </div>
                      <div>Click VLAN to filter</div>
                    </>
                  ) : (
                    <>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '3px', background: getVlanColor(selectedVlanId), marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Native (untagged)
                      </div>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '3px', background: `repeating-linear-gradient(90deg, ${getVlanColor(selectedVlanId)} 0px, ${getVlanColor(selectedVlanId)} 5px, transparent 5px, transparent 9px)`, marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Tagged
                      </div>
                      <div>Click again to clear</div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* STP Layer Legend */}
            {activeLayer === 'stp' && stpOverlay && stpOverlay.instances.length > 0 && (
              <div
                className="absolute bottom-4 right-4 z-10 rounded-md border border-border bg-popover/95 text-popover-foreground shadow-md"
                style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.85rem',
                  maxHeight: '380px',
                  overflowY: 'auto',
                  minWidth: '200px',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>STP Instance</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {stpOverlay.instances.map((inst) => {
                    const isSelected =
                      selectedStpInstance !== null &&
                      selectedStpInstance.instanceId === inst.instanceId &&
                      selectedStpInstance.instanceType === inst.instanceType;
                    return (
                      <button
                        key={`${String(inst.instanceId)}-${inst.instanceType}`}
                        onClick={() => {
                          setSelectedStpInstance({
                            instanceId: inst.instanceId,
                            instanceType: inst.instanceType,
                          });
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                          padding: '0.35rem 0.5rem',
                          border: isSelected ? '2px solid var(--primary)' : '2px solid transparent',
                          borderRadius: '4px',
                          backgroundColor: isSelected ? 'color-mix(in oklab, var(--primary) 8%, transparent)' : 'transparent',
                          cursor: 'pointer',
                          textAlign: 'left',
                          width: '100%',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <span style={{ fontWeight: isSelected ? 600 : 400 }}>
                          {stpInstanceLabel(inst)}
                        </span>
                        <span className="text-muted-foreground" style={{ fontSize: '0.75rem' }}>
                          {String(inst.deviceCount)}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="border-t border-border text-muted-foreground" style={{ marginTop: '0.75rem', paddingTop: '0.5rem', fontSize: '0.75rem' }}>
                  {selectedDeviceId ? (
                    <>
                      <div style={{ marginBottom: '0.25rem', color: 'var(--primary)', fontWeight: 600 }}>
                        Single-device view
                      </div>
                      <div style={{ marginBottom: '0.25rem' }}>
                        Color = selected device's view of its end. Click the other endpoint to compare.
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '3px', background: '#48c774', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Forwarding (both sides)
                      </div>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '3px', background: 'repeating-linear-gradient(90deg, #7a7a7a 0px, #7a7a7a 5px, transparent 5px, transparent 9px)', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Blocked (legit redundancy)
                      </div>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '4px', background: '#f14668', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Asymmetric conflict
                      </div>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '4px', background: 'repeating-linear-gradient(90deg, #ff8c00 0px, #ff8c00 5px, transparent 5px, transparent 9px)', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Variant mismatch
                      </div>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: 'rgba(72,199,116,0.45)', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Root bridge
                      </div>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: 'rgba(255,221,87,0.55)', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        Recent topology change
                      </div>
                      <div>Click a node for single-device view</div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : null}

        <div className="mt-4 scroll-mt-4" ref={panelRef}>
          {selectedEndpointId && selectedEndpointLinkId ? (
            <EndpointLinkPanel
              endpointId={selectedEndpointId}
              linkId={selectedEndpointLinkId}
              trafficRange={panelRange(pageRange)}
              onClose={clearSelectionParams}
              onEndpointSelect={(endpointId) => { selectEndpoint(endpointId, null); }}
            />
          ) : selectedEndpointId ? (
            <EndpointPanel
              endpointId={selectedEndpointId}
              trafficRange={panelRange(pageRange)}
              onClose={clearSelectionParams}
              onLinkSelect={selectEndpoint}
            />
          ) : null}
          {selectedDeviceId && (
            <DeviceDetailPanel
              trafficRange={panelRange(pageRange)}
              deviceId={selectedDeviceId}
              onClose={handleCloseDevicePanel}
              highlightedInterfaceIds={highlightedInterfaceIds}
              onInterfaceClick={handleInterfaceClick}
              interfaceConnections={interfaceConnectionMap}
              layerContext={deviceLayerContext}
            />
          )}
          {selectedLinkData && (
            <LinkDetailPanel
              trafficRange={panelRange(pageRange)}
              linkData={selectedLinkData}
              onClose={handleCloseLinkPanel}
              onDeviceSelect={handleLinkDeviceSelect}
            />
          )}
          {selectedUnconnectedInterface && (
            <InterfaceDetailPanel
              trafficRange={panelRange(pageRange)}
              deviceId={selectedUnconnectedInterface.deviceId}
              interfaceId={selectedUnconnectedInterface.interfaceId}
              onClose={handleCloseInterfacePanel}
              onBackToDevice={handleBackToDevice}
              connected={connectedInterfaceIds.has(selectedUnconnectedInterface.interfaceId)}
            />
          )}
          {!selectedDeviceId && !selectedLinkData && !selectedUnconnectedInterface && !selectedEndpointId ? (
            <div className="rounded-md border border-border bg-card p-4">
              <p className="text-sm text-muted-foreground">
                Click a device or link to view details
              </p>
            </div>
          ) : null}
        </div>

        <TopologyResyncDialog
          open={showResync}
          onOpenChange={setShowResync}
          organizationId={organizationId}
          regionId={selectedRegionId}
        />
    </PageContainer>
  );
}
